import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, finalize } from 'rxjs';

import { OpportunityApiService } from './api/opportunity.api.service';
import type { OpportunitiesDashboardStats } from './opportunities-dashboard/opportunities-dashboard.models';
import type { OpportunityStatsResponse } from './models/opportunity-list.models';

/** Root cache for opportunity dashboard statistics; HTTP delegated to {@link OpportunityApiService}. */
@Injectable({ providedIn: 'root' })
export class OpportunityStatsService {
  private readonly opportunityApi = inject(OpportunityApiService);

  private readonly _apiStats = signal<OpportunityStatsResponse | null>(null);
  private readonly _statsLoading = signal(false);
  private readonly _statsError = signal<string | null>(null);

  readonly statsLoading = this._statsLoading.asReadonly();
  readonly statsError = this._statsError.asReadonly();

  /** Present only after a successful stats response; do not treat as loaded when `null`. */
  readonly stats = computed<OpportunitiesDashboardStats | null>(() => {
    const statsResponse = this._apiStats();
    if (!statsResponse) return null;
    return {
      total: statsResponse.totalOpportunities,
      qualifiedOpportunities: statsResponse.qualifiedOpportunities,
      newInLast7Days: statsResponse.newInLast7Days,
      pipelineValueChf: statsResponse.pipelineValueChf,
      qualificationRatePercent: statsResponse.qualificationRatePercent,
    };
  });

  ensureLoaded(): void {
    if (this._apiStats() === null) {
      this.loadStats();
    }
  }

  loadStats(): void {
    this._statsLoading.set(true);
    this._statsError.set(null);
    this.opportunityApi
      .getStats()
      .pipe(
        catchError(() => {
          this._statsError.set('Failed to load opportunity statistics.');
          return EMPTY;
        }),
        finalize(() => this._statsLoading.set(false)),
      )
      .subscribe((statsResponse) => {
        this._apiStats.set(statsResponse);
        this._statsError.set(null);
      });
  }
}
